import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Search,
  Eye,
  Edit,
  Trash2,
  BookOpen,
  Calendar,
  Tag,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Star,
  Filter,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { server } from "../server";
import { useNavigate } from "react-router-dom";
import Loading from "../Components/global/Loading";

const NotesList = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");

  const [filters, setFilters] = useState({ status: "" });
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
  });

  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setSubjects(data?.data || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchChapters = async (subjectId) => {
    try {
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: { subjectId },
      });
      setChapters(data?.data || []);
    } catch (error) {
      console.error("Error fetching chapters:", error);
    }
  };

  const fetchTopics = async (subjectId, chapterId) => {
    try {
      const { data } = await axios.get(`${server}/topics/all`, {
        params: { pageNo: 1, pageSize: 100, subjectId, chapterId },
      });
      setTopics(data?.data || []);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        pageNo: pagination.pageNo,
        pageSize: pagination.pageSize,
        search: searchTerm || undefined,
        status: filters.status || undefined,
        subjectId: selectedSubject || undefined,
        chapterId: selectedChapter || undefined,
        topicId: selectedTopic || undefined,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) delete params[key];
      });

      const { data } = await axios.get(`${server}/notes/all`, { params });

      setNotes(data.data || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: Math.ceil(data.count / pagination.pageSize),
        totalItems: data.count,
      }));
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to load notes";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedSubject("");
    setSelectedChapter("");
    setSelectedTopic("");
    setSearchTerm("");
    setFilters({ status: "" });
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  useEffect(() => {
    fetchSubjects();
    fetchNotes();
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [
    pagination.pageNo,
    pagination.pageSize,
    filters,
    selectedSubject,
    selectedChapter,
    selectedTopic,
  ]);

  useEffect(() => {
    if (selectedSubject) {
      fetchChapters(selectedSubject);
      setSelectedChapter("");
      setSelectedTopic("");
      setTopics([]);
    } else {
      setChapters([]);
      setSelectedChapter("");
      setSelectedTopic("");
      setTopics([]);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedSubject && selectedChapter) {
      fetchTopics(selectedSubject, selectedChapter);
      setSelectedTopic("");
    } else {
      setTopics([]);
      setSelectedTopic("");
    }
  }, [selectedChapter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, pageNo: newPage }));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;

    try {
      await axios.delete(`${server}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast.success("Note deleted successfully");
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete note");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getContentPreview = (content) => {
    const plainText = content?.replace(/<[^>]*>/g, "") || "";
    return plainText.length > 120
      ? plainText.substring(0, 120) + "..."
      : plainText;
  };

  return (
    <div className="min-h-screen bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-7xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">All Notes</h1>
          </div>
          <p className="text-gray-400">
            Manage and view all your educational notes
          </p>
        </div>

        {/* Filters Section */}
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 mb-6 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-5 h-5 text-blue-400" />
              <span className="text-sm font-semibold text-white">
                Filter by
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  Subject
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white bg-gray-700 text-sm"
                  value={selectedSubject}
                  onChange={(e) => {
                    setSelectedSubject(e.target.value);
                    setPagination((prev) => ({ ...prev, pageNo: 1 }));
                  }}
                >
                  <option value="">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  Chapter
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-green-500 text-white bg-gray-700 text-sm"
                  value={selectedChapter}
                  onChange={(e) => {
                    setSelectedChapter(e.target.value);
                    setPagination((prev) => ({ ...prev, pageNo: 1 }));
                  }}
                  disabled={!selectedSubject}
                >
                  <option value="">All Chapters</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.id} value={chapter.id}>
                      {chapter.chapter}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  Topic
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500 text-white bg-gray-700 text-sm"
                  value={selectedTopic}
                  onChange={(e) => {
                    setSelectedTopic(e.target.value);
                    setPagination((prev) => ({ ...prev, pageNo: 1 }));
                  }}
                  disabled={!selectedChapter}
                >
                  <option value="">All Topics</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.topic}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-80">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search notes..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white bg-gray-700 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="px-4 py-2.5 border border-gray-600 rounded-lg focus:outline-none focus:border-green-500 text-white bg-gray-700 text-sm"
              >
                <option value="">All Status</option>
                <option value="true">Published</option>
                <option value="false">Draft</option>
              </select>

              {(selectedSubject ||
                selectedChapter ||
                selectedTopic ||
                searchTerm ||
                filters.status) && (
                <motion.button
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-600 text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <X className="w-4 h-4" />
                  Clear
                </motion.button>
              )}

              <motion.button
                onClick={fetchNotes}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </motion.button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Notes Grid */}
        {notes.length === 0 ? (
          <div className="text-center py-16 bg-gray-800 rounded-2xl border border-gray-700">
            <BookOpen className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              No notes found
            </h3>
            <p className="text-gray-500 text-sm">
              {searchTerm || selectedSubject
                ? "Try adjusting your filters"
                : "No notes available"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {notes.map((note) => (
                <motion.div
                  key={note?.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-800 rounded-xl p-5 border border-gray-700 hover:border-gray-600 transition-colors"
                >
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-white line-clamp-2">
                          {note?.title}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            note?.isPublished
                              ? "bg-green-500/20 text-green-300"
                              : "bg-gray-600 text-gray-300"
                          }`}
                        >
                          {note?.isPublished ? "Published" : "Draft"}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-3">
                        {getContentPreview(note?.content)}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <BookOpen className="w-4 h-4" />
                        <span>{note?.subject || "No Subject"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Tag className="w-4 h-4" />
                        <span>{note?.chapter || "No Chapter"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(note?.createdAt)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <motion.button
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          navigate("../notesDetails", {
                            state: { notesId: note?.id },
                          })
                        }
                      >
                        View Details
                      </motion.button>
                      <div className="flex items-center gap-2">
                        <motion.button
                          onClick={() =>
                            navigate("../editNote", {
                              state: { notesId: note?.id },
                            })
                          }
                          className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Edit className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleDeleteNote(note?.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-400">
                    Showing{" "}
                    <span className="text-white font-medium">
                      {(pagination.pageNo - 1) * pagination.pageSize + 1}
                    </span>{" "}
                    -{" "}
                    <span className="text-white font-medium">
                      {Math.min(
                        pagination.pageNo * pagination.pageSize,
                        pagination.totalItems
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="text-white font-medium">
                      {pagination.totalItems}
                    </span>{" "}
                    notes
                  </div>

                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={() => handlePageChange(pagination.pageNo - 1)}
                      disabled={pagination.pageNo === 1}
                      className="p-2 border border-gray-600 text-gray-400 rounded-lg hover:border-gray-500 disabled:opacity-50"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </motion.button>

                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map(
                        (_, i) => {
                          const page = i + 1;
                          return (
                            <motion.button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`w-8 h-8 rounded-lg text-sm ${
                                pagination.pageNo === page
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-400 hover:bg-gray-700"
                              }`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              {page}
                            </motion.button>
                          );
                        }
                      )}
                    </div>

                    <motion.button
                      onClick={() => handlePageChange(pagination.pageNo + 1)}
                      disabled={pagination.pageNo === pagination.totalPages}
                      className="p-2 border border-gray-600 text-gray-400 rounded-lg hover:border-gray-500 disabled:opacity-50"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>

      {loading && notes.length === 0 && <Loading />}
    </div>
  );
};

export default NotesList;
